import React from 'react';
import { motion } from 'framer-motion';

const SATScoreDisplay = ({ scoreData }) => {
    if (!scoreData) {
        return <div className="text-slate-300">Loading score data...</div>;
    }

    // Helper function to get color based on grade
    const getGradeColor = (grade) => {
        switch (grade) {
            case 'A': return 'text-green-500';
            case 'B': return 'text-blue-500';
            case 'C': return 'text-yellow-500';
            case 'D': return 'text-orange-500';
            case 'F': return 'text-red-500';
            default: return 'text-gray-500';
        }
    };

    // Helper function to get difficulty color
    const getDifficultyColor = (difficulty) => {
        switch (difficulty.toLowerCase()) {
            case 'easy': return 'text-green-500';
            case 'medium': return 'text-yellow-500';
            case 'hard': return 'text-red-500';
            default: return 'text-gray-500';
        }
    };

    return (
        <div className="w-full max-w-4xl">
            <div className="text-slate-100">
                {/* Header Section */}
                <div className="border-b border-[#3b3b3b] px-4 py-2 flex justify-between items-center">
                    <h2 className="text-xl font-bold">SAT Score Analysis</h2>
                    <span className={`px-3 py-1 rounded-full text-sm ${getDifficultyColor(scoreData["SAT difficulty"])} 
                        border border-current`}>
                        {scoreData["SAT difficulty"]} Difficulty
                    </span>
                </div>

                {/* Main Content */}
                <div className="px-4 py-4 space-y-4">
                    {/* Score Grade Section */}
                    <div className="bg-[#2b2b2b] rounded-lg p-4 border border-[#3b3b3b] hover:border-[#87bfff] 
                        transition-colors duration-200">
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="text-lg font-semibold">Your SAT Grade</h3>
                            <span className={`text-2xl font-bold ${getGradeColor(scoreData["MY SAT SCORE"]["Letter grade"])}`}>
                                {scoreData["MY SAT SCORE"]["Letter grade"]}
                            </span>
                        </div>
                        <p className="text-sm text-slate-300">
                            {scoreData["MY SAT SCORE"]["Reason"]}
                        </p>
                    </div>

                    {/* Priority Topics Section */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* English Topics */}
                        <div className="bg-[#2b2b2b] rounded-lg p-4 border border-[#3b3b3b] hover:border-[#87bfff] 
                            transition-colors duration-200">
                            <h3 className="text-lg font-semibold mb-3 text-[#87bfff]">
                                English Priority Topics
                            </h3>
                            <ul className="space-y-2">
                                {scoreData["English priority Topics"].map((topic, index) => (
                                    <motion.li
                                        key={index}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                        className="flex items-center text-sm text-slate-300"
                                    >
                                        <span className="mr-2 text-[#87bfff]">•</span>
                                        {topic}
                                    </motion.li>
                                ))}
                            </ul>
                        </div>

                        {/* Math Topics */}
                        <div className="bg-[#2b2b2b] rounded-lg p-4 border border-[#3b3b3b] hover:border-[#87bfff] 
                            transition-colors duration-200">
                            <h3 className="text-lg font-semibold mb-3 text-[#87bfff]">
                                Math Priority Topics
                            </h3>
                            <ul className="space-y-2">
                                {scoreData["Math priority Topics"].map((topic, index) => (
                                    <motion.li
                                        key={index}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                        className="flex items-center text-sm text-slate-300"
                                    >
                                        <span className="mr-2 text-[#87bfff]">•</span>
                                        {topic}
                                    </motion.li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Performance Analysis Section */}
                    <div className="bg-[#2b2b2b] rounded-lg p-4 border border-[#3b3b3b] hover:border-[#87bfff] 
                        transition-colors duration-200">
                        <h3 className="text-lg font-semibold mb-3">Performance Analysis</h3>
                        <p className="text-sm text-slate-300">
                            {scoreData["Performance analysis"]}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SATScoreDisplay;